<template>
  <tr>
    <td>
      {{ refinementName }}
      <span v-if="cloudStore.isOpen" class="cursor--pointer text-primary" @click="showEditRefinementModal">
        <i class="fa fa-pencil ml-1"></i>
      </span>
    </td>
    <td v-if="printArticle">
      <span class="text-primary cursor--pointer" @click="show">
        <i class="fa fa-info-circle"></i> {{ (inEnglish) ? printArticle.article.name_en : printArticle.article.name }}
      </span>
    </td>
    <td v-else>
      <span class="mr-2 bg-warning">
        <i class="fa fa-warning text-warning"></i> Artikel fehlt
      </span>
    </td>
    <td v-if="printArticle" class="text-right">
      {{ $filters.formatMoney(printArticle.amount * printArticle.exhibition_prices.price_net) }}
    </td>
    <td v-else class="text-right">
      {{ $filters.formatMoney(0) }}
    </td>
    <td v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED">
      <span v-if="hasUpload && configuration.upload.file_exists"
        class="text-primary cursor--pointer"
        @click="download(configuration.upload)">
        <i class="fa fa-download"></i> {{ configuration.upload.original_name }}
      </span>

      <template v-if="hasUpload && !configuration.upload.file_exists">
        <span class="text-primary">{{ configuration.upload.original_name }}</span><br>
        <span class="mr-2 bg-danger">
          <i class="fa fa-warning text-danger"></i> {{ $t('configurator.labels.file_missing_reupload') }}
        </span>
        <span v-if="cloudStore.isOpen"
          class="text-primary cursor--pointer"
          @click="showUploadModal">
          <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
        </span>
      </template>

      <template v-if="!hasUpload">
        <span class="mr-2 bg-warning">
          <i class="fa fa-warning text-warning"></i> {{ $t('configurator.labels.upload_missing') }}
        </span>
        <span v-if="cloudStore.isOpen"
          class="text-primary cursor--pointer"
          @click="showUploadModal">
          <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
        </span>
      </template>
    </td>
    <td v-else>
      <span v-if="configuration.upload" class="text-primary">
        {{ configuration.upload.original_name }} ({{ $t('configurator.labels.deleted') }})
      </span>
    </td>
    <td>
      <span v-if="hasUpload && cloudStore.isOpen"
        class="text-primary cursor--pointer"
        @click="confirmDeleteUpload(configuration.upload)">
        <i class="fa fa-trash"></i> {{ $t('configurator.buttons.delete_graphic_file') }}
      </span>
    </td>
  </tr>
  <modal :is-active="uploadModalActive" :title="$t('configurator.modals.titles.upload_graphic_file')">
    <template #body>
      <div class="form-group">
        <label for="property-images">{{ $t('configurator.form.labels.select_file') }}:</label>
        <drop-zone :id="dropzoneId" @drop.prevent="drop" @change="selectedFile"></drop-zone>
        <!--<input class="form-control" type="file" id="print-images">-->
        <p class="help-block mb-1 pb-1" style="font-weight: 400;">
          {{ $t('configurator.form.labels.selected_file') }}: <span v-if="dropzoneFile">{{ dropzoneFile.name }}</span>
        </p>
        <printing-information></printing-information>
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-md-12 text-center">
            <button class="btn btn-warning mr-2" @click="cancelUpload">
              {{ $t('configurator.buttons.cancel') }}
            </button>
            <button class="btn btn-primary" @click="upload">
              {{ $t('configurator.buttons.upload_graphic_file') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </modal>
  <EditArticleRefinementModal v-if="editRefinementModalActive"
    :article="article"
    :configuration="configuration"
    :index="index"
    :print-article="printArticle"
    @hide="hideEditRefinementModal" />
</template>

<script setup>
/*
 TODO: put upload and modal in parent component?
 */
import { computed, reactive, ref } from 'vue'
import { useMainStore } from '@/store'
import { useCloudStore } from '@/stores/cloud'
import { downloadFile } from '@/utils/file'
import Swal from 'sweetalert2'
import axios from 'axios'
import { showErrorToast, showLoadingIndicator, showSuccessToast, showWarningToast } from '@/utils/toast'
import { PRINT_CLOUD_STATUS_FINISHED, PRINT_CLOUD_STATUS_OPEN } from '@/constants'
import FormDataForm from '@/classes/FormDataForm'
import { useI18n } from 'vue-i18n'
import EditArticleRefinementModal from '@/components_new/PrintCloud/EditArticleRefinementModal.vue'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
const cloudStore = useCloudStore()
// define props
const props = defineProps({
  configuration: {
    type: Object,
    required: true
  },
  article: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
})
// define emits
const emit = defineEmits(['show'])
// define reactive props
const dropzoneFile = ref('')
const uploadModalActive = ref(false)
const uploadForm = reactive(new FormDataForm({
  file: null,
  configuration_id: props.configuration.id,
  use_graphic_for_all_prints: false
}))
const editRefinementModalActive = ref(false)
// define computed
const inEnglish = computed(() => store.inEnglish)
const cloud = computed(() => store.tmp.cloud)

const hasUpload = computed(() => {
  return (props.configuration.upload)
})

const printArticle = computed(() => {
  return props.article.prints.find((printArticle) => {
    return printArticle.configurations
      .map((config) => config.request_article_id)
      .includes(props.configuration.request_article_id)
  })
})

const refinementName = computed(() => {
  if (printArticle.value === undefined || printArticle.value === null) {
    return ''
  }

  return (inEnglish.value)
    ? printArticle.value.article_print.name_en
    : printArticle.value.article_print.name
})

const dropzoneId = computed(() => {
  return 'dropzone_config_' + props.configuration.id
})
// define methods
const show = () => {
  emit('show', printArticle.value)
}

const download = async (upload) => {
  await downloadFile(
    '/print/' + cloud.value.uuid + '/download/' + upload.id,
    upload.original_name
  )
}

const confirmDeleteUpload = (upload) => {
  Swal.fire({
    title: t('configurator.notices.titles.delete_upload'),
    text: t('configurator.notices.messages.delete_upload'),
    showCancelButton: true,
    confirmButtonText: t('configurator.buttons.yes'),
    cancelButtonText: t('configurator.buttons.no'),
  })
    .then((result) => {
      if (result.isConfirmed) {
        deleteUpload(upload)
      }
    })
}

const deleteUpload = async (upload) => {
  showLoadingIndicator(t('print_cloud.indicator.delete_upload'))
  await axios.delete('/print/' + cloud.value.uuid + '/upload/' + upload.id)
    .then(response => {
      cloudStore.data.print_articles = response.data.articles
      showSuccessToast(t('configurator.toasts.messages.upload_deleted'), t('toasts.title.success'))
    })
    .catch(err => {
      console.error(err)
      showErrorToast(t('toast.message.error'), t('toast.title.error'))
    })
}

const showUploadModal = (slot) => {
  uploadForm.slot = slot
  uploadModalActive.value = true
}

const hideUploadModal = () => {
  uploadModalActive.value = false
}

const drop = (e) => {
  dropzoneFile.value = e.dataTransfer.files[0]
}

const selectedFile = () => {
  dropzoneFile.value = document.getElementById(dropzoneId.value).files[0]
}

const upload = async () => {
  showLoadingIndicator(t('print_cloud.indicator.upload'))
  uploadForm.file = dropzoneFile.value
  await uploadForm.post('/print/' + cloud.value.uuid + '/upload')
    .then((response) => {
      cloudStore.data.print_articles = response.data.articles
      store.tmp.booth = response.data.booth
      uploadForm.resetToOriginal()
      dropzoneFile.value = ''
      hideUploadModal()
      showSuccessToast(t('configurator.toasts.messages.upload_successful'), t('toast.title.success'))
    })
    .catch(err => {
      if (err.status === 413) {
        showWarningToast(t('toast.warnings.too_large'), t('toast.title.warning'), 5000)
      } else {
        showErrorToast(t('toast.message.error'), t('toast.title.error'))
      }
    })
}

const cancelUpload = async () => {
  dropzoneFile.value = ''
  hideUploadModal()
}

const showEditRefinementModal = () => {
  editRefinementModalActive.value = true
}

const hideEditRefinementModal = () => {
  editRefinementModalActive.value = false
}
</script>